/**
 * RoyaleEndScreen
 *
 * Web end state for Puzzle Royale: a single "Puzzle Royale ended" block shown
 * in place of the live strip, with the final leaderboard staying on the battle
 * screen (per the PRD — web does NOT navigate away on end). The race strip /
 * Players tab on the right shows who won, so per UI-4 we DON'T call out a
 * winner here — just the title + your stats, all in ONE block.
 *
 * Presentational — parent passes my final row.
 */

import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import CCText from '@/Atoms/CCText';
import {FontSizes} from '@/Utils/Dimensions';

import type {PlayerRoyaleState} from '@/Screens/FunChallenges/royaleTypes';

const TEAL = '#3DAB9E';
const BROWN = '#4D3F37';
const TEXT_DARK = '#262322';
const TEXT_MUTED = '#8A7E5F';
const CREAM3 = '#EDE4CC';

interface Props {
  me?: PlayerRoyaleState | null;
  myRank?: number;
  totalPlayers: number;
  /** Host-only: show the Rematch button (server pushes a new lobby to all). */
  canRematch?: boolean;
  onRematch?: () => void;
}

export default function RoyaleEndScreen({
  me,
  myRank,
  totalPlayers,
  canRematch,
  onRematch,
}: Props) {
  return (
    <View style={styles.block}>
      <View style={styles.headerRow}>
        <CCText style={styles.title}>Puzzle Royale ended</CCText>
        {canRematch && (
          <Pressable onPress={onRematch} style={styles.rematchBtn}>
            <CCText style={styles.rematchText}>Rematch</CCText>
          </Pressable>
        )}
      </View>

      {/* Players see their own stats; spectators (no `me`) see just the title —
          the final order lives in the Race-tab leaderboard. */}
      {me && (
        <View style={styles.summary}>
          <Stat
            label="Your rank"
            value={myRank != null ? `#${myRank + 1} / ${totalPlayers}` : '—'}
          />
          <Divider />
          <Stat label="Points" value={String(me.points)} />
          <Divider />
          <Stat label="Solved" value={String(me.solved)} />
          <Divider />
          <Stat label="Best combo" value={String(me.bestCombo)} />
        </View>
      )}
      {canRematch && (
        <CCText style={styles.rematchHint}>
          Rematch starts a fresh race with the same settings — everyone here
          joins automatically.
        </CCText>
      )}
    </View>
  );
}

function Stat({label, value}: {label: string; value: string}) {
  return (
    <View style={styles.stat}>
      <CCText style={styles.statLabel}>{label}</CCText>
      <CCText style={styles.statValue}>{value}</CCText>
    </View>
  );
}

function Divider() {
  return <View style={styles.divider} />;
}

const styles = StyleSheet.create({
  // Single block: title + stats together (UI-4).
  block: {
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: CREAM3,
    padding: 18,
    gap: 16,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  title: {
    fontSize: FontSizes[20] || 20,
    fontWeight: '800',
    color: TEXT_DARK,
    flexShrink: 1,
  },
  rematchBtn: {
    backgroundColor: TEAL,
    borderRadius: 999,
    paddingHorizontal: 20,
    paddingVertical: 9,
  },
  rematchText: {
    fontSize: FontSizes[14] || 14,
    fontWeight: '800',
    color: '#fff',
  },
  rematchHint: {
    fontSize: FontSizes[12] || 12,
    color: TEXT_MUTED,
  },
  summary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  stat: {alignItems: 'center', flex: 1},
  statLabel: {
    fontSize: FontSizes[10] || 10,
    fontWeight: '700',
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  statValue: {
    fontSize: FontSizes[18] || 18,
    fontWeight: '800',
    color: BROWN,
  },
  divider: {width: 1, height: 32, backgroundColor: CREAM3},
});
